import el from "@cypherpotato/el";
import { api } from "../../api";
import { Button, PrimaryButton } from "../../components/button";
import { Card, CardNavigation } from "../../components/card";
import { ChatMessage, ChatMessages } from "../../components/chat";
import { CommandBar } from "../../components/command-bar";
import { DataPickerControl } from "../../components/data-picker";
import { Dashboard } from "../../components/dashboard";
import { Editor, EditorDescription, EditorTitle } from "../../components/editor";
import { NumberInput, TextArea } from "../../components/form";
import { Icon } from "../../components/icon";
import { JsonControl } from "../../components/json-control";
import { ModelBadge } from "../../components/model-icon";
import { ModelPickerControl } from "../../components/model-picker";
import { Playground, PlaygroundAside, PlaygroundAsideContainer, PlaygroundContent, PlaygroundDefault } from "../../components/playground";
import { HorizontalSizableContainer, HorizontalSizableSection, SizableContainer, SizableSection } from "../../components/sizable-container";
import { TabControl } from "../../components/tabcontrol";
import { BlockCodeWrapper, CheckWrapper, MutedText, Paragraph, ShimmerText } from "../../components/text";
import { displayAlert, displayFormControl } from "../../ui/alert";
import { DefaultCulture } from "../../ui/culture/default";
import { formatFloat, formatNumber } from "../../ui/format";
import { cond, valueFrom } from "../../util";
import { Badge, BadgeList, TagBadge } from "../../components/badge";

function ValidationChat() {
    const chatMessages = ChatMessages();

    return el(".chat-wrapper.validation-chat",
        chatMessages,
        {
            $functions: {
                clear() {
                    chatMessages.replaceChildren();
                },
                startMessage(role, content = "", isGenerating = false) {
                    const message = ChatMessage({
                        role,
                        initialMarkdown: content,
                        initialReasoning: "",
                        culture: DefaultCulture,
                        options: {}
                    });
                    chatMessages.append(message);
                    if (content) {
                        message.setDone();
                    } else if (isGenerating && role === "user") {
                        message.appendChild(el("span.validation-user-generation-spinner.loader", {
                            role: "status",
                            ariaLabel: "Generating user message"
                        }));
                    } else {
                        message.startImplicitThinking();
                    }
                    message.scrollIntoView({
                        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
                        block: "end"
                    });
                    return message;
                },
                startJudge(turnNumber) {
                    const status = el("span.validation-judge-status", Badge("Analyzing...", "--fr-200"), { ariaLive: "polite" });
                    const reasoning = el("div.validation-judge-reasoning", ShimmerText("The judge is evaluating this turn. Please, wait."));
                    const judge = el("section.validation-judge.analyzing",
                        el("header",
                            Icon("scales-3-line", { ariaHidden: "true" }),
                            el("strong", `Turn ${turnNumber} evaluation`),
                            status
                        ),
                        reasoning,
                        {
                            $functions: {
                                setResult(result) {
                                    const state = ["active", "at_risk", "success", "loss"].includes(result?.state)
                                        ? result.state
                                        : (result?.pass === true ? "active" : "at_risk");
                                    const stateLabels = {
                                        active: "Active",
                                        at_risk: "At risk",
                                        success: "Goal reached",
                                        loss: "Lost"
                                    };
                                    const score = formatFloat(Number(result?.score || 0) * 100);
                                    const trend = formatFloat(Number(result?.conversation_delta || 0) * 100);
                                    const risk = state === "at_risk" || state === "loss"
                                        ? ` · Low scores ${Number(result?.loss_streak || 0)}/${Number(result?.required_loss_streak || 2)}`
                                        : "";

                                    this.classList.remove("analyzing");
                                    this.classList.add(state.replace("_", "-"));
                                    status.replaceChildren(BadgeList(
                                        TagBadge(stateLabels[state]),
                                        TagBadge(`Score ${score}%`),
                                        TagBadge(`Trend ${trend}%${risk}`),
                                    ));
                                    reasoning.replaceChildren(Paragraph(result?.reasoning || "No judge reasoning was returned."));
                                },
                                setDone() {
                                    if (this.classList.contains("analyzing")) {
                                        this.classList.remove("analyzing");
                                        status.textContent = "Analysis completed without a result";
                                        reasoning.replaceChildren(MutedText("No judge result was returned for this turn."));
                                    }
                                }
                            }
                        }
                    );
                    chatMessages.append(judge);
                    judge.scrollIntoView({
                        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
                        block: "end"
                    });
                    return judge;
                },
                addStatus(message, icon = "information-line") {
                    const status = el("div.validation-chat-status",
                        Icon(icon, { ariaHidden: "true" }),
                        el("span", message),
                        { ariaLive: "polite" }
                    );
                    chatMessages.append(status);
                    status.scrollIntoView({
                        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
                        block: "end"
                    });
                }
            }
        }
    );
}

export default function () {
    const hasResults = el.state.create(false);
    const isRunning = el.state.create(false);
    const validationChat = ValidationChat();
    let currentMessage = null;
    let currentMessageRole = null;
    let currentJudge = null;
    let currentTurn = 0;
    let cancellation = null;
    let disposed = false;

    function buildRequest() {
        return {
            goal: valueFrom("validationGoal"),
            validation_criteria: valueFrom("validationCriteria")?.nullWhenEmpty(),
            model: valueFrom("validationModel"),
            max_turns: valueFrom("validationMaxTurns"),
            minimum_turns: valueFrom("validationMinimumTurns"),
            allow_user_exit: valueFrom("validationAllowUserExit"),
            judge_start_turn: valueFrom("validationJudgeStartTurn"),
            loss_threshold: valueFrom("validationLossThreshold"),
            base_threshold: valueFrom("validationBaselineThreshold"),
            profile: valueFrom("validationProfile"),
            user_sampling: {
                top_k: valueFrom("validationSamplingTopK"),
                max_decay: valueFrom("validationSamplingMaxDecay")
            },
            start: valueFrom("validationStart"),
            metadata: valueFrom("validationMetadata"),
            resources: valueFrom("validationResources"),
            external_user_id: valueFrom("validationExternalUserId")?.nullWhenEmpty()
        };
    }

    function validateRequest(data) {
        if (!data.goal?.trim()) {
            return "Provide a test goal before starting.";
        }
        if (!data.model) {
            return "Select a model before starting.";
        }
        if (data.max_turns < 2 || data.max_turns > 64) {
            return "Max turns must be between 2 and 64.";
        }
        if (data.minimum_turns < 1 || data.minimum_turns >= data.max_turns) {
            return "Minimum turns must be at least 1 and less than max turns.";
        }
        if (data.judge_start_turn < 1 || data.judge_start_turn >= data.max_turns) {
            return "Judge start turn must be at least 1 and less than max turns.";
        }
        if (!["low", "medium", "high"].includes(data.profile)) {
            return "Profile must be low, medium, or high.";
        }
        if (data.loss_threshold > data.base_threshold) {
            return "Loss threshold cannot be greater than baseline threshold.";
        }
        if (Math.abs(data.loss_threshold - data.base_threshold) <= 0.1) {
            return "The distance between loss and baseline thresholds must be greater than 0.1.";
        }
        if (!Array.isArray(data.start)) {
            return "Start must be a valid JSON array of conversation messages.";
        }
        if (!Array.isArray(data.resources) || data.resources.length > 16 || data.resources.some(resource =>
            !resource || Array.isArray(resource) || !["Text", "RemoteResource"].includes(resource.type) ||
            resource.hook !== undefined && !["OnStart", "OnTurn"].includes(resource.hook) ||
            resource.visibility !== undefined && !["Internal", "Gateway", "Both"].includes(resource.visibility) ||
            resource.cacheDuration !== undefined && (typeof resource.cacheDuration !== "number" || resource.cacheDuration < 0 || resource.cacheDuration > 21600) ||
            typeof resource.data !== "string" || !resource.data.trim())) {
            return "Resources must contain at most 16 valid objects; cacheDuration must be between 0 and 21600 seconds.";
        }
        if (!data.metadata || Array.isArray(data.metadata) || typeof data.metadata !== "object") {
            return "Metadata must be a valid JSON object.";
        }
        if (Object.values(data.metadata).some(value => typeof value !== "string")) {
            return "Every metadata value must be a string.";
        }
        return null;
    }

    function processEvent(envelope) {
        const type = envelope?.event?.type;
        const data = envelope?.event?.data || {};

        if (type?.startsWith("chat.user_message.") && currentMessageRole !== "user" && !type.endsWith("start_generation") && !type.endsWith("end_conversation") ||
            type?.startsWith("chat.assistant_message.") && currentMessageRole !== "assistant" && !type.endsWith("start_generation") ||
            type === "chat.user_message.end_conversation" && !currentMessage?.classList.contains("chat-role-user")) {
            const error = new Error(`Test stream protocol error: received ${type} while ${currentMessageRole || "no"} message is active.`);
            currentMessage?.setError(error.message);
            console.error(error);
            return;
        }

        switch (type) {
            case "chat.start":
                currentTurn = Number(data.turn_number) || currentTurn + 1;
                currentJudge = null;
                break;
            case "chat.user_message.start_generation":
                currentMessage?.setDone();
                currentMessage = validationChat.startMessage("user", "", true);
                currentMessageRole = "user";
                break;
            case "chat.user_message.reasoning":
                currentMessage?.appendReasoning(data.reasoning_content || "");
                break;
            case "chat.user_message.content":
                currentMessage?.append(data.content || "");
                break;
            case "chat.user_message.end_generation":
                currentMessage?.setDone();
                currentMessageRole = null;
                break;
            case "chat.user_message.end_conversation":
                currentMessage?.remove();
                currentMessage = null;
                currentMessageRole = null;
                break;
            case "chat.assistant_message.start_generation":
                currentMessage?.setDone();
                currentMessage = validationChat.startMessage("assistant");
                currentMessageRole = "assistant";
                break;
            case "chat.assistant_message.reasoning":
                currentMessage?.appendReasoning(data.reasoning_content || "");
                break;
            case "chat.assistant_message.content":
                currentMessage?.append(data.content || "");
                break;
            case "chat.assistant_message.end_generation":
                currentMessage?.setDone();
                currentMessageRole = null;
                break;
            case "chat.judge.turn_analysis_start":
                currentMessage?.setDone();
                currentJudge = validationChat.startJudge(currentTurn);
                break;
            case "chat.judge.turn_analysis_result_ready":
                currentJudge?.setResult(data.result);
                break;
            case "chat.judge.turn_analysis_end":
                currentJudge?.setDone();
                break;
            case "chat.validation.end": {
                const trend = formatFloat(Number(data.conversation_delta || 0) * 100);
                const endStatuses = {
                    baseline_reached: [`Goal reached. Final trend: ${trend}%.`, "checkbox-circle-line"],
                    simulated_user_declared_goal_reached: ["The simulated user declared that the goal was reached.", "checkbox-circle-line"],
                    loss_threshold_persisted: [`Test stopped after the low-score trend persisted. Final trend: ${trend}%.`, "stop-circle-line"],
                    max_turns_reached: [`Maximum turns reached without a terminal result. Final trend: ${trend}%.`, "time-line"]
                };
                const [message, icon] = endStatuses[data.reason] || ["Test ended.", "information-line"];
                validationChat.addStatus(message, icon);
                break;
            }
            case "usage_updated":
                break;
        }
    }

    async function startValidation() {
        if (isRunning.value || disposed) {
            return;
        }

        const data = buildRequest();
        const validationError = validateRequest(data);
        if (validationError) {
            displayAlert({
                title: "Test parameters",
                message: validationError,
                type: "alert-warning"
            });
            return;
        }

        validationChat.clear();
        data.start
            .filter(message => message?.role === "user" || message?.role === "assistant")
            .forEach(message => validationChat.startMessage(message.role, message.content));
        hasResults.value = true;
        isRunning.value = true;
        currentMessage = null;
        currentMessageRole = null;
        currentJudge = null;
        currentTurn = 0;
        const controller = new AbortController();
        cancellation = controller;

        try {
            await api.postReader("/api/v1/generations/agentic-tests", {
                data,
                signal: controller.signal,
                onreadline(line) {
                    const normalizedLine = line.trim();
                    if (!normalizedLine || normalizedLine.startsWith(":")) {
                        return;
                    }

                    const eventData = normalizedLine.startsWith("data:")
                        ? normalizedLine.slice(5).trim()
                        : normalizedLine;
                    if (eventData) {
                        processEvent(JSON.parse(eventData));
                    }
                }
            });
            currentMessage?.setDone();
        } catch (error) {
            if (error?.name === "AbortError") {
                currentMessage?.setDone();
                currentJudge?.setDone();
                if (!disposed) {
                    validationChat.addStatus("Test stopped.", "stop-circle-line");
                }
            } else {
                if (currentMessage) {
                    currentMessage.setError(error.message || "The test stream was interrupted.");
                } else {
                    displayAlert({
                        title: "Test failed",
                        message: error.message || "The test stream was interrupted.",
                        type: "alert-warning"
                    });
                }
                console.error(error);
            }
        } finally {
            currentMessage = null;
            currentMessageRole = null;
            currentJudge = null;
            if (cancellation === controller) {
                cancellation = null;
                isRunning.value = false;
            }
        }
    }

    const principalView = PlaygroundAsideContainer(
        SizableContainer(
            SizableSection("Goal · shared with user and judge",
                TextArea("validationGoal", null, {
                    placeholder: "Describe the goal the simulated user should pursue..."
                })
            ),
            SizableSection("Validation criteria · judge only",
                TextArea("validationCriteria", null, {
                    placeholder: "Optional conditions used only by the judge..."
                })
            ),
            SizableSection("Test parameters",
                Editor(
                    {
                        id: "validationModel",
                        value: "@xiaomi/mimo-v2.5",
                        renderValue(value) {
                            return value ? ModelBadge(value) : MutedText("No model selected");
                        },
                        manipulateValue(value, callback) {
                            displayFormControl({
                                title: "Choose model",
                                message: "Choose the routed model or AI Gateway to validate.",
                                dialogSize: "large",
                                formControlItem: ModelPickerControl({ selectedModel: value, listGateways: true }),
                                onConfirm: callback,
                                validate: [
                                    [x => !!x, "Select a model before continuing."]
                                ]
                            });
                        }
                    },
                    EditorDescription(
                        EditorTitle("Model"),
                        Paragraph("Model or gateway used for responses.")
                    )
                ),
                Editor(
                    {
                        id: "validationProfile",
                        value: "medium",
                        renderValue(value) {
                            return cond(
                                [value?.toLowerCase() == "low", Badge("Low", "var(--secondary)")],
                                [value?.toLowerCase() == "medium", Badge("Medium", "var(--primary)")],
                                [value?.toLowerCase() == "high", Badge("High", "var(--success)")],
                                [true, Badge(value)]
                            );
                        },
                        manipulateValue(value, callback) {
                            displayFormControl({
                                title: this.editorTitle,
                                message: this.editorParagraphs,
                                formControlItem: DataPickerControl({
                                    value,
                                    data: [
                                        { value: "low", text: "Low" },
                                        { value: "medium", text: "Medium" },
                                        { value: "high", text: "High" }
                                    ]
                                }),
                                onConfirm: callback,
                                validate: []
                            });
                        }
                    },
                    EditorDescription(
                        EditorTitle("Profile"),
                        Paragraph("Controls the capability and cost of the simulated user and judge models.")
                    )
                ),
                Editor(
                    {
                        id: "validationMaxTurns",
                        value: 10,
                        renderValue(value) {
                            return Button(formatNumber(value));
                        },
                        manipulateValue(value, callback) {
                            displayFormControl({
                                title: this.editorTitle,
                                message: this.editorParagraphs,
                                formControlItem: NumberInput({ value, min: 2, max: 64, step: 1 }),
                                onConfirm: callback,
                                validate: []
                            });
                        }
                    },
                    EditorDescription(
                        EditorTitle("Max turns"),
                        Paragraph("Maximum number of test turns, from 2 to 64.")
                    )
                ),
                Editor(
                    {
                        id: "validationMinimumTurns",
                        value: 1,
                        renderValue(value) {
                            return Button(formatNumber(value));
                        },
                        manipulateValue(value, callback) {
                            displayFormControl({
                                title: this.editorTitle,
                                message: this.editorParagraphs,
                                formControlItem: NumberInput({ value, min: 1, max: 63, step: 1 }),
                                onConfirm: callback,
                                validate: []
                            });
                        }
                    },
                    EditorDescription(
                        EditorTitle("Minimum turns"),
                        Paragraph("First turn when the simulated user may end the conversation. Must be less than max turns.")
                    )
                ),
                Editor(
                    {
                        id: "validationAllowUserExit",
                        value: true,
                        renderValue(value) {
                            return CheckWrapper(value, "allowed", "not allowed");
                        },
                        manipulateValue(value, callback) {
                            callback(!!!value);
                        }
                    },
                    EditorDescription(
                        EditorTitle("Allow user exit"),
                        Paragraph("Show the simulated user the conversation exit option after minimum turns.")
                    )
                ),
                Editor(
                    {
                        id: "validationJudgeStartTurn",
                        value: 1,
                        renderValue(value) {
                            return Button(formatNumber(value));
                        },
                        manipulateValue(value, callback) {
                            displayFormControl({
                                title: this.editorTitle,
                                message: this.editorParagraphs,
                                formControlItem: NumberInput({ value, min: 1, max: 63, step: 1 }),
                                onConfirm: callback,
                                validate: []
                            });
                        }
                    },
                    EditorDescription(
                        EditorTitle("Judge start turn"),
                        Paragraph("First turn evaluated by the judge. Must be less than max turns.")
                    )
                ),
                Editor(
                    {
                        id: "validationLossThreshold",
                        value: 0.3,
                        renderValue(value) {
                            return Button(formatFloat(value));
                        },
                        manipulateValue(value, callback) {
                            displayFormControl({
                                title: this.editorTitle,
                                message: this.editorParagraphs,
                                formControlItem: NumberInput({ value, min: 0.01, max: 0.99, step: 0.01 }),
                                onConfirm: callback,
                                validate: []
                            });
                        }
                    },
                    EditorDescription(
                        EditorTitle("Loss threshold"),
                        Paragraph("Accumulated trend threshold used after two consecutive low scores, from 0.01 to 0.99.")
                    )
                ),
                Editor(
                    {
                        id: "validationBaselineThreshold",
                        value: 0.9,
                        renderValue(value) {
                            return Button(formatFloat(value));
                        },
                        manipulateValue(value, callback) {
                            displayFormControl({
                                title: this.editorTitle,
                                message: this.editorParagraphs,
                                formControlItem: NumberInput({ value, min: 0.01, max: 0.99, step: 0.01 }),
                                onConfirm: callback,
                                validate: []
                            });
                        }
                    },
                    EditorDescription(
                        EditorTitle("Baseline threshold"),
                        Paragraph("Judge score required to finish the test successfully, from 0.01 to 0.99.")
                    )
                ),
                Editor(
                    {
                        id: "validationSamplingTopK",
                        value: 0.4,
                        renderValue(value) {
                            return Button(formatFloat(value));
                        },
                        manipulateValue(value, callback) {
                            displayFormControl({
                                title: this.editorTitle,
                                message: this.editorParagraphs,
                                formControlItem: NumberInput({ value, min: 0, max: 2, step: 0.01 }),
                                onConfirm: callback,
                                validate: []
                            });
                        }
                    },
                    EditorDescription(
                        EditorTitle("Sampling top K"),
                        Paragraph("Maximum sampling options for simulated user messages, from 0 to 2.")
                    )
                ),
                Editor(
                    {
                        id: "validationSamplingMaxDecay",
                        value: 0.02,
                        renderValue(value) {
                            return Button(formatFloat(value));
                        },
                        manipulateValue(value, callback) {
                            displayFormControl({
                                title: this.editorTitle,
                                message: this.editorParagraphs,
                                formControlItem: NumberInput({ value, min: 0, max: 1, step: 0.01 }),
                                onConfirm: callback,
                                validate: []
                            });
                        }
                    },
                    EditorDescription(
                        EditorTitle("Sampling max decay"),
                        Paragraph("Maximum sampling decay for simulated user messages, from 0 to 1.")
                    )
                )
            )
        )
    );
    const startMessagesView = PlaygroundAsideContainer(
        SizableContainer(
            SizableSection("Start messages",
                JsonControl({
                    id: "validationStart",
                    value: [],
                    ariaLabel: "Starting conversation JSON"
                })
            )
        )
    );
    const metadataView = PlaygroundAsideContainer(
        SizableContainer(
            SizableSection("External user ID",
                TextArea("validationExternalUserId", null, {
                    placeholder: "Optional external user ID forwarded to the gateway..."
                })
            ),
            SizableSection("External resources",
                JsonControl({
                    id: "validationResources",
                    value: [],
                    ariaLabel: "External resources JSON"
                })
            ),
            SizableSection("Metadata",
                JsonControl({
                    id: "validationMetadata",
                    value: {},
                    ariaLabel: "Test metadata JSON"
                })
            )
        )
    );
    const root = Dashboard(
        { classList: ["full-size", "validations-page"] },
        Card(
            CardNavigation(
                CommandBar(
                    el.state.listening([isRunning], () =>
                        isRunning.value
                            ? PrimaryButton(Icon("stop-fill"), "Stop test", {
                                onClick() {
                                    cancellation?.abort();
                                }
                            })
                            : PrimaryButton(Icon("play-mini-fill"), "Run test", {
                                onClick() {
                                    startValidation();
                                }
                            })
                    ),
                    Button(Icon("braces-line"), "View code", {
                        onClick() {
                            displayAlert({
                                title: "Agentic Test code",
                                dialogSize: "large",
                                message: BlockCodeWrapper("Shell request", `curl --no-buffer '${new URL("/api/v1/generations/agentic-tests", window.env.apiEndpoint)}' \\
    -H "Authorization: Bearer $AIVAX_API_KEY" \\
    -H 'Accept: text/event-stream' \\
    -H 'Content-Type: application/json' \\
    -d '${JSON.stringify(buildRequest(), null, 4).replaceAll("'", "'\\''")}'`, "bash")
                            });
                        }
                    })
                )
            ),
            Playground(
                HorizontalSizableContainer(
                    HorizontalSizableSection(
                        { initialSize: "400px" },
                        PlaygroundAside(
                            TabControl([
                                {
                                    title: "Principal",
                                    icon: Icon("equalizer-2-line"),
                                    contents: principalView
                                },
                                {
                                    title: "Start messages",
                                    icon: Icon("chat-1-line"),
                                    contents: startMessagesView
                                },
                                {
                                    title: "Metadata",
                                    icon: Icon("braces-line"),
                                    contents: metadataView
                                }
                            ], { type: "vertical" })
                        )
                    ),
                    HorizontalSizableSection(
                        PlaygroundContent(
                            { classList: ["validation-chat-content"] },
                            el.state.listening([hasResults], () =>
                                hasResults.value
                                    ? validationChat
                                    : PlaygroundDefault("chat-check-line", "Run a test to see the simulated conversation.")
                            )
                        )
                    )
                )
            )
        )
    );

    const observer = new MutationObserver(() => {
        if (!root.isConnected) {
            disposed = true;
            observer.disconnect();
            cancellation?.abort();
        }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return root;
}
